// Seletor do número por onde a conversa vai sair. Usado onde a loja INICIA o contato
// (nova conversa, campanha) — em conversa já existente não há escolha: a resposta sai
// obrigatoriamente pelo número que recebeu, porque a janela de 24h da Meta é por par
// (número, cliente) e responder por outro número devolve 131047.
//
// Some da tela quando a loja tem um número só: escolher entre uma opção é ruído.
import { useEffect } from 'react';
import { Select } from '@/components/ui/Select';
import { PhoneIcon } from '@/components/icons';
import { useListWhatsappNumbersQuery, type StoreWhatsappNumber } from '../api';
import styles from './WhatsappNumberSelect.module.css';

interface Props {
  storeId: string;
  /** `''` = usar o principal da loja. */
  value: string;
  onChange: (numberId: string) => void;
  disabled?: boolean;
  /** id do <select>, para o `htmlFor` do rótulo de quem usa. */
  id?: string;
  /**
   * Oferece "Número principal da loja" como opção e NÃO pré-seleciona nada.
   *
   * Serve para a campanha, onde não escolher tem significado próprio: o principal é
   * resolvido a cada disparo, então trocar o principal da loja passa a valer para as
   * campanhas em andamento. Fixar o número no momento da criação congelaria isso.
   */
  allowDefault?: boolean;
}

function rotular(n: StoreWhatsappNumber): string {
  const numero = n.displayNumber ?? n.phoneNumberId;
  const nome = n.verifiedName ? `, ${n.verifiedName}` : '';
  return `${numero}${nome}${n.isDefault ? ' (principal)' : ''}`;
}

/**
 * Devolve os números que a loja pode usar para iniciar conversa. Token inválido fica
 * de fora: a Meta já recusou (190) e enviar por ele falharia depois da escolha.
 */
export function useSendableNumbers(storeId: string, skip = false) {
  const { data, isLoading } = useListWhatsappNumbersQuery(storeId, { skip: skip || !storeId });
  const numeros = (data ?? []).filter((n) => n.active && !n.tokenInvalid);
  return { numeros, isLoading, principal: numeros.find((n) => n.isDefault) };
}

export function WhatsappNumberSelect({
  storeId,
  value,
  onChange,
  disabled,
  id,
  allowDefault,
}: Props) {
  const { numeros, principal } = useSendableNumbers(storeId);

  // Pré-seleciona o principal. Também corrige a seleção quando a loja muda e o número
  // escolhido pertence à loja anterior — enviaria pelo número de outra loja.
  useEffect(() => {
    if (allowDefault || numeros.length === 0) return;
    if (value && numeros.some((n) => n.id === value)) return;
    onChange(principal?.id ?? numeros[0].id);
  }, [allowDefault, numeros, principal, value, onChange]);

  // Com `allowDefault`, a escolha vira do usuário: se ele nunca abriu o seletor, o
  // valor fica vazio e quem consome mantém o comportamento anterior.
  useEffect(() => {
    if (!allowDefault || !value) return;
    if (!numeros.some((n) => n.id === value)) onChange('');
  }, [allowDefault, numeros, value, onChange]);

  if (numeros.length < 2) return null;

  const opcoes = numeros.map((n) => ({ value: n.id, label: rotular(n) }));

  return (
    <>
      <Select
        id={id}
        leadingIcon={<PhoneIcon size={14} />}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        options={
          allowDefault
            ? [{ value: '', label: 'Número principal da loja' }, ...opcoes]
            : opcoes
        }
      />
      <p className={styles.hint}>
        {allowDefault
          ? 'Sem escolher, sai pelo principal da loja no momento de cada disparo.'
          : 'A conversa fica presa a este número: as respostas seguintes saem sempre por ele.'}
      </p>
    </>
  );
}
